/** Angular */
import { Injectable } from '@angular/core';
/** Rxjs */
import { Observable } from 'rxjs';
/** Services */
import { BydRhsAbstractService } from '../../../shared/services/byd-rhs-abstract.service';
/** Sixense */
import { BydModel } from '@sixense/byd-core-service';
/** Models */
import { DsOrganisationFormModel } from '../models/ds-organisation-form-model';

@Injectable({
  providedIn: 'root',
})
export class DsOrganisationFormsService extends BydRhsAbstractService {
  /**
   * Implement inherited abstract member 'url' from BydRhsAbstractService
   */
  public path = '';

  /**
   * Get organisation form model by Id.
   *
   * @param organisationId Id of the organisation
   * @param modelId id of the model we require
   * @returns Observable type `<DsOrganisationFormModel>`
   */
  public getOrganisationFormModelById(organisationId: number, modelId: number): Observable<DsOrganisationFormModel> {
    this.path = `/organisations/${organisationId}/forms/${modelId}`;
    return this.getModel<DsOrganisationFormModel>();
  }

  /**
   * Update form template model.
   *
   * @param  organisationId Organisation ID
   * @param  formId Form ID
   * @param  name new name
   * @param  fields new fields
   * @param  iconId new icon ID
   * @returns Observable type `<boolean>`
   */
  public updateFormTemplateModel(
    organisationId: number,
    formId: number,
    name: string,
    fields: Record<string, unknown>,
    iconId: number
  ): Observable<boolean> {
    this.path = `/organisations/${organisationId}/forms/${formId}`;
    return this.put<boolean>({ body: { name, fields, iconId } });
  }

  /**
   * Delete form template model.
   *
   * @param  organisationId Organisation ID
   * @param  formId Form ID
   * @returns Observable type `<boolean>`
   */
  public deleteFormTemplateModel(organisationId: number, formId: number): Observable<boolean> {
    this.path = `/organisations/${organisationId}/forms/${formId}`;
    return this.delete<boolean>();
  }

  /**
   * Search for Form Models by name.
   *
   * @param organisationId ID of organisation
   * @param name String used to search Form Models
   * @returns Observable type `<BydModel<DsOrganisationFormModel>>`
   */
  public getFormTemplateModelFilterByName(
    organisationId: number,
    name: string
  ): Observable<BydModel<DsOrganisationFormModel>> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(1000, 1);
    this.path = `/organisations/${organisationId}/forms/filter`;
    return this.postModels<DsOrganisationFormModel>({ body: { name }, headers });
  }

  /**
   * Returns list of names and ids of form models.
   *
   * @param organisationId Organisation ID
   * @param ids Ids to search for
   * @returns Observable<BydModel<DsOrganisationFormModel>>
   */
  public getFormTemplateModelsFromIds(
    organisationId: number,
    ids: number[]
  ): Observable<BydModel<DsOrganisationFormModel>> {
    const headers = this.bydRhsHeadersService.setHeadersWithPagination(1000, 1);
    this.path = `/organisations/${organisationId}/forms/ids`;
    return this.postModels<DsOrganisationFormModel>({ body: { ids }, headers });
  }

  /**
   * Create DsOrganisationFormModel instance.
   *
   * @param  jsonObject The flat json object.
   * @returns an `<DsOrganisationFormModel>` instance.
   */
  // TODO:  Set up the correct type
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public createNewInstance(jsonObject: any): DsOrganisationFormModel {
    return new DsOrganisationFormModel(jsonObject);
  }
}
